/* eslint-disable no-param-reassign */

import { createSlice } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import listSaga from './list';
import byIdSaga from './byId';
import childrenSaga from './children';
import createSaga, { CommentCreateData } from './create';
import updateSaga, { CommentUpdateData } from './update';
import deleteSaga from './delete';
import { BaseState, createCRUDModel, createCRUDStates } from '../creator';
import { RootState } from '../index';
import { PaginatedResponse } from '../types';
import { User } from '../auth';
import { SubCategory } from '../../hooks/api/subCategories';
import { CategoryShort } from '../../hooks/api/categories';
import { Article } from '../article';

export const moduleName = 'comment';

export type CommentStatus = 'published' | 'deactivated' | 'draft';
export const CommentStatusTitle: Record<CommentStatus, string> = {
  published: 'Published',
  deactivated: 'Hidden',
  draft: 'Draft',
};

export interface CommentFile {
  id: string;
  originalName: string;
  location: string;
  mimeType: string;
  createdAt: string;
}

export interface Comment {
  id: string;
  parentComment: string | null;
  text: string;
  numberOfLikes: number;
  numberOfDislikes: number;
  status?: CommentStatus;
  files: {
    id: string;
    originalName: string;
    location: string;
    mimeType: string;
    createdAt: string;
  }[],
  user: User,
  numberOfComments: number;
  numberOfCommentsTotal: number;
  category: CategoryShort,
  subCategory: SubCategory,
  article: Article,
  createdAt: string;
  updatedAt: string;
}

export interface State {
  list: BaseState<PaginatedResponse<Comment>>,
  byId: BaseState<Comment>,
  children: BaseState<PaginatedResponse<Comment, { parentId: string }>>,
  create: BaseState<CommentCreateData>,
  update: BaseState<CommentUpdateData>,
  delete: BaseState<{ id: string }>
}

const models = ['list', 'children', 'byId', 'create', 'update', 'delete'];

export const defaultState: State = createCRUDStates<State>(models);

export const comment = createSlice({
  name: moduleName,
  initialState: defaultState,
  reducers: createCRUDModel(models, moduleName),
});

export const { actions } = comment;

export function useComment(): State {
  return useSelector((state: RootState) => state[moduleName]);
}

export function* saga() {
  yield* listSaga();
  yield* byIdSaga();
  yield* childrenSaga();
  yield* createSaga();
  yield* updateSaga();
  yield* deleteSaga();
}
